/**
 * L1J Web Client - QuickSlotBar Component (Lineage Classic Style)
 *
 * 우하단에 표시되는 단축키 바:
 *   - F5 ~ F12 슬롯 (2줄 × 4)
 *   - 각 슬롯은 정사각 + 단축키 라벨
 *   - 인벤토리에서 아이템을 **끌어다 놓아** 등록한다
 *
 * 조작:
 *   - 인벤토리 슬롯 → 퀵슬롯 드래그 : 등록 (GameScene이 중계한다)
 *   - 퀵슬롯 → 다른 퀵슬롯 드래그   : 자리 맞바꾸기
 *   - 퀵슬롯 → 바 바깥으로 드래그   : 등록 해제
 *   - 우클릭                        : 등록 해제
 *   - 클릭 / F5~F12                 : 사용
 *
 * 슬롯이 들고 있는 것은 **아이템 objId**다. 그 objId가 인벤토리에서 사라지면
 * (다 써서 없어지거나, 다시 주워 새 objId가 붙으면) 슬롯을 지우지 않고 흐리게
 * 남겨 둔다. 같은 종류의 아이템이 다시 들어오면 GameScene이 objId를 재바인딩한다
 * (`syncQuickSlots()`). 포션처럼 소모하고 다시 채우는 아이템의 등록이 매번
 * 풀리지 않게 하려는 것이다.
 *
 * 카메라 스크롤과 무관하게 화면에 고정.
 */

import Phaser from 'phaser';
import { resolveNpcName, resolveItemLabel } from '../../utils/nameResolver';
import {
  itemIconKind,
  itemIconTextureKey,
  loadItemIcon,
  preloadItemIcons,
} from '../../data/itemIcons';
import { DragGhost } from './DragGhost';

export interface QuickSlotItem {
  /** 아이템 objId. 서버로 나가는 use_item의 itemId가 이 값이다. */
  id: number;
  gfxId?: number;
  count?: number;
  /** 표시명 (인벤토리에서 해석이 끝난 이름). */
  label?: string;
  enchantLevel?: number;
  identified?: boolean;
  equipped?: boolean;
  /**
   * 인벤토리에 지금 없는 아이템. 흐리게 표시하고 눌러도 사용하지 않는다.
   * (등록 자체는 남겨 두고 같은 종류가 다시 들어오면 재바인딩된다.)
   */
  missing?: boolean;
}

/** 장착 중 표시 색 — 인벤토리와 같은 규칙(초록 = 장착)을 쓴다. */
const EQUIPPED_TEXT_COLOR = '#7ce87c';
const EQUIPPED_BORDER_COLOR = 0x4ad24a;

const SLOT_BORDER_COLOR = 0x4a4a6a;
const SLOT_HOVER_COLOR = 0xffd700;
/** 드래그 중인 아이템이 이 슬롯 위에 있다 — 놓으면 여기 들어간다. */
const SLOT_DROP_COLOR = 0x66ddff;
const SLOT_BG_COLOR = 0x1a1a2e;
const SLOT_BG_EQUIPPED_COLOR = 0x24382a;

/** 드래그로 인정하는 최소 이동 거리(px). 이보다 짧으면 클릭(사용)이다. */
const DRAG_THRESHOLD = 5;

export class QuickSlotBar extends Phaser.GameObjects.Container {
  private slotSize: number = 40;
  private slotGap: number = 4;
  private cols: number = 4;
  private rows: number = 2;
  /** 단축키 라벨 시작 번호 (F5 ~ F12) */
  private hotkeyStart: number = 5;
  private slots: QuickSlot[] = [];
  private items: (QuickSlotItem | null)[] = new Array(this.cols * this.rows).fill(null);
  private panelPadding: number = 8;

  private panelWidth: number = 0;
  private panelHeight: number = 0;

  /** 마우스를 올린 슬롯의 이름을 보여주는 툴팁 (바 위쪽). */
  private tooltip!: Phaser.GameObjects.Text;

  private ghost!: DragGhost;

  /** 드래그 후보(눌렀지만 아직 임계값을 넘지 않은 상태). */
  private pressedSlot: number | null = null;
  private pressedAt: { x: number; y: number } | null = null;
  /** 실제로 끌기 시작한 슬롯. null이면 드래그 중이 아니다. */
  private draggingSlot: number | null = null;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;
  private onScenePointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private onScenePointerUp!: (pointer: Phaser.Input.Pointer) => void;

  private clickCallback: ((slotIndex: number, item: QuickSlotItem | null) => void) | null = null;
  private changeCallback: (() => void) | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.sceneInput = scene.input;
    scene.add.existing(this);

    this.setScrollFactor(0);
    this.setDepth(100);

    this.createUI();
    this.registerDragInput();

    // 아이콘 23종은 통틀어도 몇 KB라 한 번에 받아둔다. 등록 순간 텍스처가 이미
    // 있으면 setItem()이 동기 경로로 빠져 깜빡임이 없다.
    void preloadItemIcons(scene);
  }

  private createUI(): void {
    const totalW = this.cols * this.slotSize + (this.cols - 1) * this.slotGap;
    const totalH = this.rows * this.slotSize + (this.rows - 1) * this.slotGap;

    // 배경 패널 (가시성 향상)
    this.panelWidth = totalW + this.panelPadding * 2;
    this.panelHeight = totalH + this.panelPadding * 2;
    const panel = this.scene.add.graphics();
    panel.fillStyle(0x0a0a18, 0.85);
    panel.fillRoundedRect(-this.panelWidth / 2, -this.panelHeight / 2, this.panelWidth, this.panelHeight, 6);
    panel.lineStyle(2, 0x4a3a14, 1);
    panel.strokeRoundedRect(-this.panelWidth / 2, -this.panelHeight / 2, this.panelWidth, this.panelHeight, 6);
    this.add(panel);

    // 첫 슬롯의 **중심** 좌표.
    // QuickSlot의 배경은 Rectangle이라 원점이 중앙이다. 그리드 좌상단을 그대로
    // 첫 슬롯 좌표로 쓰면 그리드 전체가 slotSize/2 만큼 왼쪽·위로 밀려
    // 배경 패널과 어긋난다.
    const startX = -totalW / 2 + this.slotSize / 2;
    const startY = -totalH / 2 + this.slotSize / 2;

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const idx = r * this.cols + c;
        const slotX = startX + c * (this.slotSize + this.slotGap);
        const slotY = startY + r * (this.slotSize + this.slotGap);

        const slot = new QuickSlot(this.scene, slotX, slotY, this.slotSize, idx, this.hotkeyStart, {
          onPress: (slotIndex, pointer) => this.handleSlotPress(slotIndex, pointer),
          onRightClick: (slotIndex) => this.handleSlotRightClick(slotIndex),
          onHover: (slotIndex) => this.showTooltip(slotIndex),
          onHoverEnd: (slotIndex) => this.hideTooltip(slotIndex),
        });
        this.slots.push(slot);
        this.add(slot);
      }
    }

    // 툴팁 — 바 바로 위 (아이템 이름). 기본 숨김.
    this.tooltip = this.scene.add.text(0, -this.panelHeight / 2 - 6, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    this.tooltip.setOrigin(0.5, 1);
    this.tooltip.setVisible(false);
    this.add(this.tooltip);

    // 드래그 고스트는 마지막에 붙여 슬롯들 위에 그려지게 한다.
    this.ghost = new DragGhost(this.scene, this.slotSize - 4);
    this.add(this.ghost);
  }

  // ==================== 등록 / 조회 ====================

  /**
   * 슬롯에 아이템 설정
   *
   * @param notify 사용자 조작으로 바뀐 경우 true — 변경 콜백(영속화)이 돈다.
   *               서버 갱신 반영처럼 프로그램이 다시 그리는 경우는 false.
   */
  public setItem(slotIndex: number, item: QuickSlotItem | null, notify: boolean = false): void {
    if (slotIndex < 0 || slotIndex >= this.slots.length) return;
    this.items[slotIndex] = item;
    this.slots[slotIndex].setItem(item);
    if (notify) this.changeCallback?.();
  }

  public getItem(slotIndex: number): QuickSlotItem | null {
    if (slotIndex < 0 || slotIndex >= this.items.length) return null;
    return this.items[slotIndex];
  }

  /** 등록 현황 사본. 영속화할 때 쓴다. */
  public getItems(): (QuickSlotItem | null)[] {
    return this.items.map((it) => (it ? { ...it } : null));
  }

  public getSlotCount(): number {
    return this.slots.length;
  }

  /**
   * 화면 좌표가 어느 슬롯 위인지. 드롭 대상 판정용이며 없으면 null.
   * 이 컨테이너는 scrollFactor 0이라 화면 좌표에서 바로 로컬로 환산한다.
   */
  public getSlotIndexAt(screenX: number, screenY: number): number | null {
    if (!this.visible) return null;
    const localX = screenX - this.x;
    const localY = screenY - this.y;
    const half = this.slotSize / 2;

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (Math.abs(localX - slot.x) <= half && Math.abs(localY - slot.y) <= half) {
        return i;
      }
    }
    return null;
  }

  /** 화면 좌표가 바 패널 안인지. 바깥으로 끌어낸 드롭을 가려낼 때 쓴다. */
  public containsPoint(screenX: number, screenY: number): boolean {
    if (!this.visible) return false;
    return (
      Math.abs(screenX - this.x) <= this.panelWidth / 2 &&
      Math.abs(screenY - this.y) <= this.panelHeight / 2
    );
  }

  /**
   * 드롭 예정 슬롯을 강조한다. 인벤토리에서 끌고 오는 동안 GameScene이 호출한다.
   * null이면 강조 해제.
   */
  public setDropTarget(slotIndex: number | null): void {
    this.slots.forEach((slot, i) => slot.setDropTarget(i === slotIndex));
  }

  // ==================== 콜백 ====================

  /**
   * 슬롯 클릭(사용) 이벤트. 드래그로 끝난 포인터는 여기로 오지 않는다.
   */
  public onSlotClick(callback: (slotIndex: number, item: QuickSlotItem | null) => void): void {
    this.clickCallback = callback;
  }

  /** 사용자가 등록을 바꿨다 (드래그 이동 / 해제). 영속화 훅. */
  public onChange(callback: () => void): void {
    this.changeCallback = callback;
  }

  /**
   * 단축키로 슬롯 트리거 (F5~F12)
   */
  public triggerSlot(slotIndex: number): void {
    if (slotIndex < 0 || slotIndex >= this.slots.length) return;
    this.slots[slotIndex].flash();
    this.clickCallback?.(slotIndex, this.items[slotIndex]);
  }

  // ==================== 드래그 ====================

  private handleSlotPress(slotIndex: number, pointer: Phaser.Input.Pointer): void {
    // 빈 슬롯도 눌림은 기록한다 — 놓았을 때 "빈 슬롯 클릭"으로 처리해야
    // 사용 콜백이 null을 받고 조용히 끝난다.
    this.pressedSlot = slotIndex;
    this.pressedAt = { x: pointer.x, y: pointer.y };
  }

  private handleSlotRightClick(slotIndex: number): void {
    if (!this.items[slotIndex]) return;
    this.setItem(slotIndex, null, true);
    this.hideTooltip(slotIndex);
  }

  private registerDragInput(): void {
    this.onScenePointerMove = (pointer) => {
      if (this.pressedSlot === null || !this.pressedAt) return;

      if (this.draggingSlot === null) {
        // 임계값을 넘기 전까지는 아직 클릭 후보다.
        const moved = Phaser.Math.Distance.Between(
          this.pressedAt.x,
          this.pressedAt.y,
          pointer.x,
          pointer.y
        );
        if (moved < DRAG_THRESHOLD) return;

        const item = this.items[this.pressedSlot];
        if (!item) {
          // 빈 슬롯에서 시작한 드래그는 잡을 것이 없다 — 후보를 접는다.
          this.pressedSlot = null;
          this.pressedAt = null;
          return;
        }

        this.draggingSlot = this.pressedSlot;
        this.slots[this.draggingSlot].setDragging(true);
        this.hideTooltip(this.draggingSlot);
        this.ghost.show(
          { gfxId: item.gfxId, label: item.label, count: item.count },
          pointer.x - this.x,
          pointer.y - this.y
        );
      }

      this.ghost.followTo(pointer.x - this.x, pointer.y - this.y);
      this.setDropTarget(this.getSlotIndexAt(pointer.x, pointer.y));
    };

    this.onScenePointerUp = (pointer) => {
      const pressed = this.pressedSlot;
      const dragging = this.draggingSlot;
      this.pressedSlot = null;
      this.pressedAt = null;
      this.draggingSlot = null;

      if (dragging !== null) {
        this.slots[dragging].setDragging(false);
        this.ghost.hide();
        this.setDropTarget(null);
        this.finishDrag(dragging, pointer);
        return;
      }

      if (pressed === null) return;
      // 끌지 않았다 = 클릭. 사용은 pointerup에서 확정한다 —
      // pointerdown에서 곧바로 쓰면 드래그를 시작할 수가 없다.
      const overSlot = this.getSlotIndexAt(pointer.x, pointer.y);
      if (overSlot !== pressed) return; // 슬롯 밖에서 뗐다 → 취소

      this.slots[pressed].flash();
      this.clickCallback?.(pressed, this.items[pressed]);
    };

    this.sceneInput.on('pointermove', this.onScenePointerMove);
    this.sceneInput.on('pointerup', this.onScenePointerUp);

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('pointermove', this.onScenePointerMove);
      this.sceneInput.off('pointerup', this.onScenePointerUp);
    });
  }

  /** 퀵슬롯에서 시작한 드래그의 종착지 처리: 자리 맞바꾸기 또는 등록 해제. */
  private finishDrag(from: number, pointer: Phaser.Input.Pointer): void {
    const target = this.getSlotIndexAt(pointer.x, pointer.y);

    if (target === null) {
      // 바 안의 빈 여백에 떨어뜨린 것은 실수로 보고 아무 일도 하지 않는다.
      // 바 **바깥**으로 끌어낸 경우에만 등록을 해제한다.
      if (this.containsPoint(pointer.x, pointer.y)) return;
      this.setItem(from, null, true);
      return;
    }

    if (target === from) return;

    const moved = this.items[from];
    const displaced = this.items[target];
    this.setItem(target, moved);
    this.setItem(from, displaced);
    this.changeCallback?.();
  }

  // ==================== 툴팁 ====================

  private showTooltip(slotIndex: number): void {
    if (this.draggingSlot !== null) return;
    const item = this.items[slotIndex];
    if (!item) return;

    const enchant = item.enchantLevel && item.enchantLevel > 0 ? `+${item.enchantLevel} ` : '';
    const count = item.count && item.count > 1 ? ` x${item.count}` : '';
    const state = item.missing ? ' (없음)' : item.equipped ? ' [장착 중]' : '';
    this.tooltip.setText(`${enchant}${item.label ?? item.id}${count}${state}`);
    this.tooltip.setVisible(true);
  }

  private hideTooltip(slotIndex: number): void {
    // 옆 슬롯으로 넘어가 pointerover가 먼저 온 경우 새 툴팁을 지우지 않도록,
    // 지금 떠 있는 것이 이 슬롯의 툴팁일 때만 지운다.
    if (!this.tooltip.visible) return;
    const item = this.items[slotIndex];
    if (item) {
      const label = item.label ?? String(item.id);
      if (!this.tooltip.text.includes(label)) return;
    }
    this.tooltip.setVisible(false);
  }
}

/**
 * 단축키 슬롯 (내부 클래스)
 */
interface QuickSlotHandlers {
  onPress: (slotIndex: number, pointer: Phaser.Input.Pointer) => void;
  onRightClick: (slotIndex: number) => void;
  onHover: (slotIndex: number) => void;
  onHoverEnd: (slotIndex: number) => void;
}

class QuickSlot extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  /** 카테고리 SVG 아이콘. 텍스처를 못 받으면 숨기고 iconText로 폴백한다. */
  private iconImage!: Phaser.GameObjects.Image;
  /** 아이콘 폴백 텍스트. 로드 실패 시에만 보인다. */
  private iconText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  private enchantText!: Phaser.GameObjects.Text;
  private equippedBadge!: Phaser.GameObjects.Text;
  private hotkeyLabel!: Phaser.GameObjects.Text;
  private slotIndex: number;
  private item: QuickSlotItem | null = null;

  private isHovered: boolean = false;
  private isDropTarget: boolean = false;
  private isEquipped: boolean = false;
  /** 이 슬롯의 아이템을 끌고 있는 중. 원본은 흐리게 남긴다. */
  private isDragging: boolean = false;

  /** 아이콘을 그릴 한 변의 길이. setTexture()마다 다시 적용해야 한다. */
  private iconSize: number;

  /** 늦게 도착한 아이콘 텍스처가 바뀐 내용에 붙지 않도록 하는 토큰. */
  private iconToken: number = 0;

  private handlers: QuickSlotHandlers;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    size: number,
    slotIndex: number,
    hotkeyStart: number,
    handlers: QuickSlotHandlers
  ) {
    super(scene, x, y);
    this.slotIndex = slotIndex;
    this.handlers = handlers;
    this.iconSize = size - 12;
    this.createUI(size, hotkeyStart);
    scene.add.existing(this);
  }

  private createUI(size: number, hotkeyStart: number): void {
    // 슬롯 배경
    this.bg = this.scene.add.rectangle(0, 0, size, size, SLOT_BG_COLOR, 0.9);
    this.bg.setStrokeStyle(1, SLOT_BORDER_COLOR);
    this.add(this.bg);

    // 아이템 아이콘. 텍스처가 정해지기 전에는 숨겨 둔다 —
    // 빈 키로 만들면 Phaser가 '__MISSING' 체크무늬를 그린다.
    this.iconImage = this.scene.add.image(0, 2, '__DEFAULT');
    this.iconImage.setVisible(false);
    this.add(this.iconImage);

    this.iconText = this.scene.add.text(0, 2, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    this.iconText.setOrigin(0.5);
    this.add(this.iconText);

    // 개수 (우하단)
    this.countText = this.scene.add.text(size / 2 - 2, size / 2 - 2, '', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    this.countText.setOrigin(1, 1);
    this.add(this.countText);

    // 인챈트 (좌하단) — 좌상단은 단축키 라벨이 차지한다.
    this.enchantText = this.scene.add.text(-size / 2 + 2, size / 2 - 2, '', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    this.enchantText.setOrigin(0, 1);
    this.add(this.enchantText);

    // 장착 표시 뱃지 (우상단)
    this.equippedBadge = this.scene.add.text(size / 2 - 2, -size / 2 + 2, 'E', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: EQUIPPED_TEXT_COLOR,
      fontStyle: 'bold',
      backgroundColor: '#12301c',
      padding: { x: 2, y: 1 },
    });
    this.equippedBadge.setOrigin(1, 0);
    this.equippedBadge.setVisible(false);
    this.add(this.equippedBadge);

    // 단축키 라벨 (F5~F12)
    const hotkeyNum = this.slotIndex + hotkeyStart;
    this.hotkeyLabel = this.scene.add.text(-size / 2 + 3, -size / 2 + 3, `F${hotkeyNum}`, {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    this.hotkeyLabel.setOrigin(0, 0);
    this.add(this.hotkeyLabel);

    // 클릭/드래그 인터랙션
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다
    // (카메라 스크롤 중 클릭 히트테스트가 어긋나는 문제 방지).
    const hitArea = this.scene.add.rectangle(0, 0, size, size, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.setScrollFactor(0);
    hitArea.on('pointerover', () => {
      this.isHovered = true;
      this.refreshBorder();
      this.handlers.onHover(this.slotIndex);
    });
    hitArea.on('pointerout', () => {
      this.isHovered = false;
      this.refreshBorder();
      this.handlers.onHoverEnd(this.slotIndex);
    });
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.handlers.onRightClick(this.slotIndex);
        return;
      }
      this.handlers.onPress(this.slotIndex, pointer);
    });
    this.add(hitArea);
  }

  public setItem(item: QuickSlotItem | null): void {
    this.item = item;
    this.iconToken++;

    if (!item) {
      this.iconImage.setVisible(false);
      this.iconText.setText('');
      this.countText.setText('');
      this.enchantText.setText('');
      this.equippedBadge.setVisible(false);
      this.isEquipped = false;
      this.bg.setFillStyle(SLOT_BG_COLOR, 0.9);
      this.refreshBorder();
      return;
    }

    this.applyIcon(item);

    this.countText.setText(item.count && item.count > 1 ? `x${item.count}` : '');

    if (item.enchantLevel && item.enchantLevel > 0) {
      this.enchantText.setText(`+${item.enchantLevel}`);
      this.enchantText.setStyle({ color: '#ffd700' });
    } else if (item.enchantLevel && item.enchantLevel < 0) {
      this.enchantText.setText(`${item.enchantLevel}`);
      this.enchantText.setStyle({ color: '#ff4444' });
    } else {
      this.enchantText.setText('');
    }

    this.isEquipped = item.equipped === true;
    this.equippedBadge.setVisible(this.isEquipped);
    this.bg.setFillStyle(this.isEquipped ? SLOT_BG_EQUIPPED_COLOR : SLOT_BG_COLOR, 0.9);
    this.refreshBorder();
    this.applyAlpha();
  }

  /**
   * 인벤토리에 없는 아이템(missing)과 미감정 아이템은 흐리게, 끌고 있는 원본은
   * 더 흐리게 그린다.
   */
  private applyAlpha(): void {
    let alpha = 1;
    if (this.item?.missing) alpha = 0.35;
    else if (this.item && this.item.identified === false) alpha = 0.6;
    if (this.isDragging) alpha = Math.min(alpha, 0.3);

    this.iconImage.setAlpha(alpha);
    this.iconText.setAlpha(alpha);
    this.countText.setAlpha(alpha);
    this.enchantText.setAlpha(alpha);
  }

  private applyIcon(item: QuickSlotItem): void {
    // label에 들어올 수 있는 $숫자 / ($숫자) 패턴을 한글로 바꾼 뒤 아이콘 판정에 쓴다.
    // 매핑이 없으면 원본 그대로 넘어가고 gfxId 쪽 판정이 답을 낸다.
    let name = resolveNpcName(item.label ?? '');
    if (name.includes('($')) {
      name = name.replace(/\(\$\d+\)/g, (m) => resolveItemLabel(m));
    }

    const kind = itemIconKind(item.gfxId ?? 0, name);
    const key = itemIconTextureKey(kind);
    const token = this.iconToken;

    if (this.scene.textures.exists(key)) {
      this.showIconTexture(key);
      return;
    }

    // 아직 없으면 받아 온다. 그 사이에는 폴백 문자를 띄워 칸이 비지 않게 한다.
    this.iconImage.setVisible(false);
    this.iconText.setText('*');
    loadItemIcon(this.scene, kind)
      .then((loadedKey) => {
        if (token !== this.iconToken) return; // 그 사이 슬롯 내용이 바뀌었다
        this.showIconTexture(loadedKey);
      })
      .catch(() => {
        /* 폴백 문자가 이미 떠 있다 */
      });
  }

  private showIconTexture(key: string): void {
    this.iconImage.setTexture(key);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 크기를 다시 잡는다.
    this.iconImage.setDisplaySize(this.iconSize, this.iconSize);
    this.iconImage.setVisible(true);
    this.iconText.setText('');
    this.applyAlpha();
  }

  public setDropTarget(isTarget: boolean): void {
    if (this.isDropTarget === isTarget) return;
    this.isDropTarget = isTarget;
    this.refreshBorder();
  }

  public setDragging(dragging: boolean): void {
    this.isDragging = dragging;
    this.applyAlpha();
  }

  /** 짧은 시각 효과 (사용 표시). */
  public flash(): void {
    this.bg.setStrokeStyle(2, SLOT_HOVER_COLOR);
    this.scene.time.delayedCall(150, () => this.refreshBorder());
  }

  /**
   * 테두리를 한 곳에서만 그린다.
   * 우선순위: 드롭 대상 > 마우스 오버 > 장착 > 기본.
   */
  private refreshBorder(): void {
    if (this.isDropTarget) {
      this.bg.setStrokeStyle(2, SLOT_DROP_COLOR);
    } else if (this.isHovered) {
      this.bg.setStrokeStyle(2, SLOT_HOVER_COLOR);
    } else if (this.isEquipped) {
      this.bg.setStrokeStyle(2, EQUIPPED_BORDER_COLOR);
    } else {
      this.bg.setStrokeStyle(1, SLOT_BORDER_COLOR);
    }
  }
}
